import { Slice } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";

/**
 * Apply an AI result to a document range through the editor's OWN parser — never as plain text.
 *
 * The history behind this is worth keeping: a raw `replaceWith(schema.text(...))` inserted a
 * markdown result as literal characters, threw outright when the result contained block
 * content (a list, a heading), and bypassed the single collaborative write path so the change
 * never reached other peers correctly. So: parse the markdown into a real document fragment
 * with the same parser the editor reads markdown with, then `replaceRange` with
 * `Slice.maxOpen`, which lets the fragment's open ends splice into the surrounding block
 * instead of forcing a paragraph break at both edges.
 *
 * `insertOnly` inserts at `from` and leaves the selection intact — the shape a "add this after
 * the selection" action needs.
 */
export function applyAiResult(
  editor: Editor,
  markdown: string,
  range: { from: number; to: number },
  options: { insertOnly?: boolean } = {},
): boolean {
  const text = markdown.trim();
  // An empty result applied over a selection is a silent DELETION of that selection — the one
  // outcome nobody asked for. Refuse instead.
  if (text.length === 0) {
    return false;
  }

  const { state, view } = editor;
  const from = Math.max(0, Math.min(range.from, state.doc.content.size));
  const to = Math.max(from, Math.min(range.to, state.doc.content.size));

  // Parse through Tiptap's markdown storage so the result honours every extension this
  // editor actually has — a widget fence, an instruction block, a conversation link.
  const parsed = parseMarkdown(editor, text);
  if (!parsed) {
    return false;
  }

  const transaction = state.tr.replaceRange(
    from,
    options.insertOnly ? from : to,
    Slice.maxOpen(parsed),
  );
  transaction.setMeta("aiResult", true);
  view.dispatch(transaction);
  return true;
}

/**
 * Markdown → a ProseMirror `Fragment`, using the editor's own markdown MANAGER.
 *
 * The manager is what knows about this editor's extensions — its widget fences, its
 * instruction blocks, its conversation links. Parsing with anything else would produce a
 * document in a schema this editor does not have. When it is absent (an editor built without
 * the Markdown extension) this returns null and the caller declines the apply rather than
 * writing something the schema cannot describe.
 */
function parseMarkdown(editor: Editor, markdown: string) {
  const storage = (editor.storage as unknown as Record<string, unknown>).markdown as
    | { manager?: { parse?: (input: string) => unknown } }
    | undefined;
  const parsed = storage?.manager?.parse?.(markdown);
  if (!parsed || typeof parsed !== "object") {
    return null;
  }
  try {
    return editor.schema.nodeFromJSON(parsed).content;
  } catch {
    // A result the schema cannot describe costs the reader that result, never the document.
    return null;
  }
}

/**
 * The plain-text offsets of a selection, alongside its positions.
 *
 * A model is handed text and answers text, so a range expressed in ProseMirror positions means
 * nothing to it: `textBetween` over `[0, from)` gives the character index where the selection
 * starts once node boundaries are gone. `\n` as the block separator matches what the markdown
 * projection produces, so the two agree about where a paragraph ends.
 */
export function selectionOffsets(editor: Editor): {
  text: string;
  from: number;
  to: number;
  startOffset: number;
  endOffset: number;
} | null {
  const { state } = editor;
  const { from, to, empty } = state.selection;
  if (empty) {
    return null;
  }
  const before = state.doc.textBetween(0, from, "\n", "\n");
  const text = state.doc.textBetween(from, to, "\n", "\n");
  return {
    text,
    from,
    to,
    startOffset: before.length,
    endOffset: before.length + text.length,
  };
}
